<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Media Request</h1>
        </div>

        <div class="modal-body no-pad" v-if="mediaRequest">
            <div class="flex h-full">
                <div class="media-request-sidebar h-full">
                    <div class="mb-4 text-center">
                        <span class="fa-layers fa-4x mb-3">
                            <i class="fa fa-circle"></i>
                            <i class="fas fa-user fa-inverse" data-fa-transform="shrink-6.5"></i>
                        </span>
                        <p class="text-lg font-light">Details</p>
                    </div>

                    <div class="mb-3">
                        <label>Authorisation Status</label>
                        <p>
                            <span class="text-green" v-if="mediaRequest.req_authorised">Authorised</span>
                            <span class="text-red" v-else>Requires Authorisation</span>
                        </p>

                        <label>ID</label>
                        <p>{{ mediaRequest.req_id }}</p>

                        <label>Name</label>
                        <p>{{ authorName }}</p>

                        <label>Email Address</label>
                        <p>{{ authorEmail }}</p>

                        <label>Publication</label>
                        <p>{{ authorPublication }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Matches</label>
                        <p>{{ mediaRequest.req_matches }}</p>

                        <label>Email Opens Unique</label>
                        <p>{{ emailOpens.unique }}</p>

                        <label>Email Opens Total</label>
                        <p>{{ emailOpens.total }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Telephone Number</label>
                        <p>{{ mediaRequest.req_tel }}</p>
                    </div>

                    <div class="mb-3" v-if="extractedKeywords && extractedKeywords.keywords.length > 0">
                        <tab-modal label="Keywords" :tab-items="extractedKeywords.keywords"></tab-modal>
                    </div>
                </div>

                <div class="media-request-body">
                    <h2>Update Media Request</h2>

                    <div class="form-field">
                        <label>Subject</label>
                        <input v-model="mediaRequest.req_subject" type="text">
                    </div>

                    <div class="form-field">
                        <label>Brief</label>
                        <textarea v-model="mediaRequest.req_brief" rows="10"></textarea>
                    </div>

                    <div class="form-field">
                        <label>Update Request Date</label>
                        <simple-date-time-picker
                            v-model="mediaRequest.req_datetime">
                        </simple-date-time-picker>
                    </div>

                    <div class="form-field">
                        <label>Update Request Deadline</label>
                        <simple-date-time-picker
                            v-model="mediaRequest.req_deadline">
                        </simple-date-time-picker>
                    </div>

                    <div class="form-field">
                        <label>Sector</label>
                        <picker-selector
                            placeholder="Select Sectors"
                            v-model="mediaRequest.req_sectors"
                            input-name="sectors[]"
                            @update:modelValue="addSectors"
                            :options="sectors">
                        </picker-selector>
                    </div>
                    <div class="form-field">
                        <label>Location</label>
                        <picker-selector
                            placeholder="Select Locations"
                            v-model="mediaRequest.req_locations"
                            input-name="locations[]"
                            @update:modelValue="addLocations"
                            :options="locations">
                        </picker-selector>
                    </div>

                    <fieldset class="border px-4 pb-4 my-4">
                        <legend class="font-bold">Replies</legend>
                        <div class="form-field">
                            <label>Reply Address</label>
                            <input :value="replyAddress" type="text">
                        </div>

                        <div class="form-field">
                            <label>Reply Address Link</label>
                            <input :value="replyAddressLink" type="text">
                        </div>
                    </fieldset>
                </div>
            </div>
        </div>
        <div class="modal-footer">
            <div class="flex">
                <div class="left text-left">
                    <div v-if="requestHasBeenSent">
                        <button class="btn-primary muted" type="button" @click="showRepliesList">
                            View Replies
                        </button>
                    </div>

                    <div v-if="!requestHasBeenSent">
                        <button class="btn-danger muted" type="button" @click="handleDeleteRequest">Delete</button>
                    </div>
                </div>

                <div class="right text-right">
                    <button class="btn-danger muted" type="button" @click="handleAuthorisationStatus"
                            v-if="mediaRequest && !mediaRequest.req_authorised">
                        Authorise The Request
                    </button>
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="saveForm">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import {useStore} from "vuex"

export default {
    name: 'MediaRequestView',
    data() {
        return {
            mediaRequest: null,
            allReplies: [],
            emailOpens: {
                total: 0,
                unique: 0,
            },
            extractedKeywords: null,
        }
    },
    mounted() {
        this.loadData()
        window.addEventListener('keyup', this.handleKeypress)
    },
    beforeDestroy() {
        window.removeEventListener('keyup', this.handleKeypress)
    },
    methods: {
        loadData() {
            axios.get(route('mediarequests.show', this.$route.params.mediaRequestId))
                .then(response => {
                    this.mediaRequest = response.data.mediaRequest
                    this.allReplies = this.mediaRequest.replies
                    this.emailOpens.unique = response.data.emailOpensUnique
                    this.emailOpens.total = response.data.emailOpensTotal
                    this.extractedKeywords = this.mediaRequest && this.mediaRequest.keyword
                    this.setMetaTitle(this.mediaRequest.req_subject + ' | JournoLink Control')
                })
                .catch(e => {
                    this.$bus.$emit('alert.danger', 'Unable to find media request')
                })
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.setMetaTitle('List Media Requests | JournoLink Control')
            this.$root.goBack()
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         * Update media request
         * @return {void}
         */
        saveForm() {
            axios.put(route('mediarequests.update', this.$route.params.mediaRequestId),
                this.formatMediaRequestForSave(this.mediaRequest)
            ).then(response => {
                this.$bus.$emit('alert.success', 'Update Success')
            })
                .catch(e => {
                    console.log(e)
                })
        },

        showRepliesList() {
            this.$router.push({ name: 'mediarequests.replies.list' })
        },
        /**
         * enable replies view rendering
         * @param  {Number} countReplies
         * @return {Boolean}
         */
        isDisabled(countReplies) {
            return countReplies == 0
        },
        /**
         * Authorise a media request
         * @return {void}
         */
        handleAuthorisationStatus() {
            let text = 'This action will authorise the media request'
            this.alert.fire({
                title: 'Are you sure?',
                text: text,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.put(route('mediarequests.authorise', this.$route.params.mediaRequestId), {}).then(response => {
                            this.$bus.$emit('alert.success', 'Authorisation update success')
                            this.mediaRequest.req_authorised = response.data.mediaRequest.req_authorised
                        }).catch(e => {
                            this.$bus.$emit('alert.danger', 'Authorisation update failed')
                        })
                    }
                })
        },
        /**
         * Format the media request object for save
         *
         * @param  {Object} mediaRequest
         * @return {Object}
         */
        formatMediaRequestForSave(mediaRequest) {
            let thisRequest = _.clone(mediaRequest)
            thisRequest.req_datetime = thisRequest.req_datetime && moment(thisRequest.req_datetime).format('YYYY-MM-DD HH:mm:ss')
            thisRequest.req_deadline = thisRequest.req_deadline && moment(thisRequest.req_deadline).format('YYYY-MM-DD HH:mm:ss')

            return thisRequest
        },
        addLocations(ids) {
            this.mediaRequest.req_locations = ids
        },
        addSectors(ids) {
            this.mediaRequest.req_sectors = ids
        },
        /**
         * Handle a delete event for this request
         *
         * @return {void}
         */
        handleDeleteRequest() {
            if (this.requestHasBeenSent) {
                return
            }

            this.alert.fire({
                title: 'Are you sure?',
                text: 'Are you sure you wish to delete this media request?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('mediarequests.delete', this.$route.params.mediaRequestId), {}).then(response => {
                            this.$bus.$emit('alert.success', 'Media request has been deleted')
                            this.closeView()
                        }).catch(e => {
                            this.$bus.$emit('alert.danger', 'Delete failed')
                        })
                    }
                })
        },
    },
    computed: {

        sectors () {
            const store = useStore()
            return store.state.sectors ?? []
        },

        locations () {
            const store = useStore()
            return store.state.locations ?? []
        },

        /**
         * Determine if the request has been sent
         *
         * @return {Boolean}
         */
        requestHasBeenSent() {
            return this.mediaRequest && this.mediaRequest.req_authorised && moment(this.mediaRequest.req_datetime).isBefore(moment().now)
        },
        /**
         * Return the email address to be used to reply to this request
         *
         * @returns {string}
         */
        replyAddress() {
            return 'request-' + this.mediaRequest.req_secret + '@journolinkmail.com'
        },
        /**
         * Return the link to open a new email to the reply address
         *
         * @returns {string}
         */
        replyAddressLink() {
            return 'mailto:' + this.replyAddress + '?subject=Re: ' + this.mediaRequest.req_subject
        },
        /**
         * Return the name of the author.
         *
         * @return {String}
         */
        authorName() {
            return this.mediaRequest.user ? this.mediaRequest.user.profile.name : this.mediaRequest.req_name
        },
        /**
         * Return the email address of the author.
         *
         * @return {String}
         */
        authorEmail() {
            return this.mediaRequest.user ? this.mediaRequest.user.email : this.mediaRequest.req_email
        },
        /**
         * Return the publication name of the author.
         *
         * @return {String}
         */
        authorPublication() {
            return this.mediaRequest.user && this.mediaRequest.user.publication ? this.mediaRequest.user.publication.title : this.mediaRequest.req_publication
        },
    },
    watch: {
        mediaRequest: {
            handler(to) {
                if (!moment(to.req_datetime, 'YYYY-MM-DDTHH:mm:ss', true).isValid()) {
                    this.mediaRequest.req_datetime = moment(to.req_datetime).toISOString(true)
                }
                if (!moment(to.req_deadline, 'YYYY-MM-DDTHH:mm:ss', true).isValid()) {
                    this.mediaRequest.req_deadline = moment(to.req_deadline).toISOString(true)
                }
            },
            deep: true,
        },
    }
}
</script>

<style lang="less">
.media-request-sidebar {
    padding: 15px;
    width: 350px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.media-request-body {
    flex: 1;
    padding: 15px;

    .brief {
        margin: 1em 0;
    }
}

.media-request-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
}
</style>
